import { z } from "zod";
import { ISODateSchema, ULIDSchema } from "./primitives.js";
import { UserIdSchema } from "./user.js";
// The 5 fixed smart shelves every household starts with. Order here doubles as
// the fallback ordering used when a shelf id is absent from ShelfConfig.order
// (see order's comment below) — unlisted shelves render after listed ones, in
// this order, then custom shelves in creation order.
export const BUILTIN_SHELF_IDS = [
    "want-to-try",
    "most-cooked",
    "recently-added",
    "under-30",
    "not-in-a-while",
];
export const ShelfFilterSchema = z
    .object({
    tagIds: z.array(ULIDSchema("tag")).optional(),
    categoryIds: z.array(ULIDSchema("category")).optional(),
    cuisineId: ULIDSchema("cuisine").optional(),
    dietFlags: z.array(ULIDSchema("diet")).optional(),
    maxTotalTimeMin: z.number().int().positive().max(10080).optional(),
    difficulty: z.enum(["easy", "medium", "hard"]).optional(),
})
    .passthrough();
export const CustomShelfSchema = z
    .object({
    id: ULIDSchema("shelf"),
    name: z.string().min(1, "name is required").max(60, "name must be ≤ 60 chars"),
    filter: ShelfFilterSchema,
})
    .passthrough();
// Household-shared shelf-config document (single row per household). Governs
// smart-shelf ordering, hidden shelves, and user-defined custom shelves on the
// library home. Read shape is passthrough (forward-compatible); the PUT request
// shape below is strict (rejects server-controlled fields).
export const ShelfConfigSchema = z
    .object({
    schemaVersion: z.literal(1).default(1),
    // Shelf ids (builtin + custom) in display order. A shelf id absent from this
    // array still renders — after the listed ones, in BUILTIN_SHELF_IDS order,
    // then custom shelves in creation order.
    order: z.array(z.string().max(40)).max(30).default([]),
    hidden: z.array(z.string().max(40)).max(30).default([]),
    custom: z.array(CustomShelfSchema).max(20).default([]),
    updatedAt: ISODateSchema,
    updatedBy: UserIdSchema,
})
    .passthrough();
// PUT /shelf-config body. updatedAt/updatedBy are server-controlled (set from
// the authenticated caller + server clock), so they're omitted from the
// request shape and unknown keys are rejected outright.
export const PutShelfConfigRequestSchema = ShelfConfigSchema.omit({
    updatedAt: true,
    updatedBy: true,
}).strict();
export const DEFAULT_SHELF_CONFIG = {
    order: [],
    hidden: [],
    custom: [],
};
//# sourceMappingURL=shelf-config.js.map